import { type ReactNode } from 'react';
import { Select } from '@/components/ui/select';
import { useAuth } from '@/hooks/use-auth';
import type {
  CreatorCategory,
  CreatorSex,
  CreatorStatus,
  CreatorType,
} from '@/types';
import { categoryLabel } from '../utils/creator-formatters';
import {
  CATEGORIES_BY_TYPE,
  STATUSES,
  changeType,
  type EditFormState,
} from './creator-edit-fields.helpers';

type Props = {
  form: EditFormState;
  setForm: (s: EditFormState) => void;
};

function TypeSelect({ form, setForm }: Props): ReactNode {
  return (
    <Select
      label="Tipo"
      value={form.creator_type}
      onChange={(e) => setForm(changeType(form, e.target.value as CreatorType))}
    >
      <option value="persona">Persona</option>
      <option value="empresa">Empresa</option>
    </Select>
  );
}

function CategorySelect({ form, setForm }: Props): ReactNode {
  return (
    <Select
      label="Categoría"
      value={form.category}
      onChange={(e) =>
        setForm({
          ...form,
          category: Number(e.target.value) as CreatorCategory,
        })
      }
    >
      {CATEGORIES_BY_TYPE[form.creator_type].map((c) => (
        <option key={c} value={c}>
          {categoryLabel(form.creator_type, c)}
        </option>
      ))}
    </Select>
  );
}

function SexSelect({ form, setForm }: Props): ReactNode {
  if (form.creator_type === 'empresa') return null;
  return (
    <Select
      label="Sexo"
      value={form.sex || 'H'}
      onChange={(e) => setForm({ ...form, sex: e.target.value as CreatorSex })}
    >
      <option value="H">H</option>
      <option value="M">M</option>
    </Select>
  );
}

function StatusSelect({ form, setForm }: Props): ReactNode {
  return (
    <Select
      label="Status"
      value={form.status}
      onChange={(e) =>
        setForm({ ...form, status: e.target.value as CreatorStatus })
      }
    >
      {STATUSES.map((st) => (
        <option key={st.value} value={st.value}>
          {st.label}
        </option>
      ))}
    </Select>
  );
}

export function EditSelects({ form, setForm }: Props): ReactNode {
  const { isAdmin } = useAuth();
  return (
    <>
      <TypeSelect form={form} setForm={setForm} />
      <CategorySelect form={form} setForm={setForm} />
      <SexSelect form={form} setForm={setForm} />
      {isAdmin && <StatusSelect form={form} setForm={setForm} />}
    </>
  );
}
